import { BenchmarkingItemWithUnit, BenchmarkingReportSettings } from '@/hooks'
import { MultiChart } from '@organisms'

import { getProcessedData } from '@/components/data/Benchmarking/utils/getProcessedData'
import { getTopEntityIds } from '@/components/data/Benchmarking/utils/getTopEntityIds'
import { getUnitFromBenchmarkingData } from '@/components/data/Benchmarking/utils/getUnitFromBenchmarkingData'
import { formatComparedToTimeline, Timeline } from '@/utils/date'
import { formatDateForChart } from '@/utils/formatDateForChart'
import { formatValueWithUnit } from '@/utils/formatValueWithUnit'
import { numberWithCommas } from '@/utils/numberWithCommas'
import { shortenNumber } from '@/utils/shortenNumber'
import { ComponentProps, useMemo } from 'react'

type BenchmarkingChartProps = {
    data: BenchmarkingItemWithUnit[]
    reportSettings: BenchmarkingReportSettings
    currentTimeline: Timeline
    baselineYearTimeline?: Timeline
    previousYearTimeline?: Timeline
}

export const BenchmarkingChart: React.FC<BenchmarkingChartProps> = ({
    data,
    reportSettings,
    currentTimeline,
    baselineYearTimeline,
    previousYearTimeline,
}) => {
    const basis = reportSettings.basis
    const unit = useMemo(
        () =>
            getUnitFromBenchmarkingData(
                data,
                reportSettings.applySquareFootage,
                basis
            ),
        [basis, data, reportSettings.applySquareFootage]
    )
    const allEntityIds = useMemo(() => getTopEntityIds(data, 20), [data])

    const baselineYearLabel = baselineYearTimeline
        ? formatComparedToTimeline(baselineYearTimeline)
        : ''
    const previousYearLabel = previousYearTimeline
        ? formatComparedToTimeline(previousYearTimeline)
        : ''

    const processedData = useMemo(() => {
        return getProcessedData(data, reportSettings, {
            baselineYear: baselineYearLabel,
            previousYear: previousYearLabel,
            currentYear: formatComparedToTimeline(currentTimeline),
        })
    }, [
        data,
        reportSettings,
        baselineYearLabel,
        previousYearLabel,
        currentTimeline,
    ])

    const renderTooltipItem: ComponentProps<
        typeof MultiChart
    >['renderTooltipItem'] = (entry) => {
        const entityName =
            data.find((entity) => entity.entityId.toString() === entry.dataKey)
                ?.entityName || entry.dataKey
        return (
            <>
                <div className="flex items-center gap-1">
                    <div
                        className="h-2 w-2 shrink-0 rounded-full bg-[currentColor]"
                        style={{
                            color: entry.color,
                        }}
                    />
                    <div className="text-label-xsmall whitespace-nowrap">
                        {entityName} ({unit})
                    </div>
                </div>
                <div className="text-body-small ml-4 text-right font-bold">
                    {typeof entry.value === 'number'
                        ? numberWithCommas(entry.value, 2)
                        : entry.value}
                </div>
            </>
        )
    }

    const getEntityById = (key: string) => {
        const entity = data.find(
            (item) => item.entityId.toString() === key.toString()
        )

        return entity
            ? { key, dataKey: key, name: entity.entityName }
            : undefined
    }

    const xKey = reportSettings.analysisType === 'trend' ? 'date' : 'entityId'
    const filterLineChartKeys =
        reportSettings.analysisType === 'trend' ? () => allEntityIds : undefined
    const getLineProps =
        reportSettings.analysisType === 'trend' ? getEntityById : undefined
    const tooltipTitleFormatter =
        reportSettings.analysisType === 'trend'
            ? formatDateForChart
            : (key: string) => getEntityById(key)?.name ?? ''

    return (
        <MultiChart
            data={processedData}
            lineChartData={processedData}
            xKey={xKey}
            filterLineChartKeys={filterLineChartKeys}
            getLineProps={getLineProps}
            tooltipTitleFormatter={tooltipTitleFormatter}
            xAxisTickFormatter={tooltipTitleFormatter}
            renderTooltipItem={renderTooltipItem}
            lineYAxisTickFormatter={(number: number) => {
                const { value, sign } = shortenNumber(number)
                const label = formatValueWithUnit({
                    value,
                    sign,
                    unit,
                })
                return label.replace(/ /g, '\u00A0')
            }}
        />
    )
}
